
import React, { useEffect } from 'react';

interface KVKKModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const KVKKModal: React.FC<KVKKModalProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    // Lock body scroll when modal is open
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup in case the component unmounts while open
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Do not render anything if the modal is closed
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        aria-hidden="true"
      ></div>

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-y-auto">
        {/* Header */}
        <header className="sticky top-0 bg-gradient-to-br from-[#0B1D3A] via-[#0F2847] to-[#0B1D3A] px-8 py-6 flex items-center justify-between border-b border-[#D4AF37]/20">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-[#D4AF37] to-[#C9A961] rounded-lg flex items-center justify-center">
              <i className="ri-shield-check-line text-xl text-white" aria-hidden="true"></i>
            </div>
            <h2 className="text-xl font-bold text-white">KVKK Aydınlatma Metni</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all duration-300"
            aria-label="Close modal"
          >
            <i className="ri-close-line text-xl" aria-hidden="true"></i>
          </button>
        </header>

        {/* Content */}
        <div className="px-8 py-8">
          <div className="space-y-6 text-gray-700 leading-relaxed text-sm">
            <div className="text-center mb-6">
              <h3 className="text-lg font-bold text-[#0B1D3A] uppercase tracking-wide">
                PRIME SALES PRO
              </h3>
              <p className="text-base font-semibold text-[#0B1D3A] mt-1">
                KİŞİSEL VERİLERİN KORUNMASI VE İŞLENMESİ AYDINLATMA METNİ
              </p>
            </div>

            <p>
              Prime Sales Pro olarak, kişisel verilerinizin güvenliğine ve gizliliğine
              büyük önem vermekteyiz. Bu Aydınlatma Metni, 6698 sayılı Kişisel Verilerin
              Korunması Kanunu ("KVKK") kapsamında, veri sorumlusu sıfatıyla kişisel
              verilerinizin hangi amaçlarla işlendiği, kimlerle paylaşılabileceği,
              hangi yöntemlerle toplandığı ve haklarınız hakkında sizleri bilgilendirmek
              amacıyla hazırlanmıştır.
            </p>

            {/* Section 1 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">1. Veri Sorumlusu</h4>
              <p>6698 sayılı KVKK uyarınca kişisel verileriniz, veri sorumlusu olarak:</p>
              <p className="font-semibold text-[#0B1D3A] mt-2 ml-4">Prime Sales Pro</p>
              <p className="mt-2">tarafından aşağıda açıklanan kapsamda işlenebilecektir.</p>
            </section>

            <hr className="border-gray-200" />

            {/* Section 2 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">2. İşlenen Kişisel Veriler</h4>
              <p className="mb-3">Prime Sales Pro tarafından aşağıdaki kişisel veriler işlenebilir:</p>
              <ul className="space-y-1 ml-4 list-none">
                {[
                  'Ad ve soyad',
                  'Telefon numarası',
                  'E-posta adresi',
                  'Şirket adı',
                  'Görev / unvan bilgisi',
                  'IP adresi ve web sitesi kullanım bilgileri',
                  'İletişim formu üzerinden ilettiğiniz mesaj içerikleri',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i
                      className="ri-checkbox-blank-circle-fill text-[#D4AF37] text-xs mt-1.5 flex-shrink-0"
                      aria-hidden="true"
                    ></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <hr className="border-gray-200" />

            {/* Section 3 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">3. Kişisel Verilerin İşlenme Amaçları</h4>
              <p className="mb-3">Toplanan kişisel verileriniz aşağıdaki amaçlarla işlenebilir:</p>
              <ul className="space-y-1 ml-4 list-none">
                {[
                  'Hizmetlerimiz hakkında bilgi vermek',
                  'Teklif hazırlamak ve iletmek',
                  'Müşteri ilişkileri süreçlerini yürütmek',
                  'Satış, danışmanlık ve destek süreçlerini yönetmek',
                  'Talep ve sorularınızı yanıtlamak',
                  'Sözleşme süreçlerini yürütmek',
                  'Hizmet kalitesini geliştirmek',
                  'Yasal yükümlülüklerimizi yerine getirmek',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i
                      className="ri-checkbox-blank-circle-fill text-[#D4AF37] text-xs mt-1.5 flex-shrink-0"
                      aria-hidden="true"
                    ></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <hr className="border-gray-200" />

            {/* Section 4 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">
                4. Kişisel Verilerin Toplanma Yöntemi ve Hukuki Sebebi
              </h4>
              <p className="mb-3">Kişisel verileriniz;</p>
              <ul className="space-y-1 ml-4 mb-4 list-none">
                {[
                  'Web sitesi iletişim formları',
                  'E-posta iletişimi',
                  'Telefon görüşmeleri',
                  'Sosyal medya iletişimleri',
                  'Çerezler ve web sitesi kullanım araçları',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i
                      className="ri-checkbox-blank-circle-fill text-[#D4AF37] text-xs mt-1.5 flex-shrink-0"
                      aria-hidden="true"
                    ></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mb-3">aracılığıyla elektronik ortamda toplanmaktadır.</p>
              <p className="mb-3">KVKK'nın 5. ve 6. maddelerinde belirtilen;</p>
              <ul className="space-y-1 ml-4 list-none">
                {[
                  'Sözleşmenin kurulması veya ifası',
                  'Veri sorumlusunun meşru menfaati',
                  'Açık rıza verilmesi',
                  'Hukuki yükümlülüklerin yerine getirilmesi',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i
                      className="ri-checkbox-blank-circle-fill text-[#D4AF37] text-xs mt-1.5 flex-shrink-0"
                      aria-hidden="true"
                    ></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-3">hukuki sebeplerine dayanarak işlenmektedir.</p>
            </section>

            <hr className="border-gray-200" />

            {/* Section 5 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">5. Kişisel Verilerin Aktarılması</h4>
              <p className="mb-3">Kişisel verileriniz;</p>
              <ul className="space-y-1 ml-4 mb-4 list-none">
                {[
                  'Hizmet aldığımız teknoloji ve altyapı sağlayıcıları',
                  'Hosting ve e-posta hizmet sağlayıcıları',
                  'Hukuki yükümlülükler kapsamında yetkili kamu kurumları',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i
                      className="ri-checkbox-blank-circle-fill text-[#D4AF37] text-xs mt-1.5 flex-shrink-0"
                      aria-hidden="true"
                    ></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p>ile sınırlı olmak üzere paylaşılabilir.</p>
              <p className="mt-3 font-medium text-[#0B1DA3]">
                Prime Sales Pro, kişisel verilerinizi üçüncü kişilere satmaz veya ticari amaçla
                paylaşmaz.
              </p>
            </section>

            <hr className="border-gray-200" />

            {/* Section 6 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">6. KVKK Kapsamındaki Haklarınız</h4>
              <p className="mb-3">
                KVKK'nın 11. maddesi kapsamında aşağıdaki haklara sahipsiniz:
              </p>
              <ul className="space-y-1 ml-4 list-none">
                {[
                  'Kişisel verilerinizin işlenip işlenmediğini öğrenme',
                  'İşlenmişse bilgi talep etme',
                  'İşlenme amacını öğrenme',
                  'Eksik veya yanlış işlenmişse düzeltilmesini isteme',
                  'Silinmesini veya yok edilmesini isteme',
                  'İşlemenin sınırlandırılmasını talep etme',
                  'Kanuna aykırı işleme nedeniyle zarara uğramanız halinde tazminat talep etme',
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <i
                      className="ri-checkbox-blank-circle-fill text-[#D4AF37] text-xs mt-1.5 flex-shrink-0"
                      aria-hidden="true"
                    ></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </section>

            <hr className="border-gray-200" />

            {/* Section 7 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">7. İletişim</h4>
              <p className="mb-3">
                KVKK kapsamındaki taleplerinizi aşağıdaki iletişim adresi üzerinden iletebilirsiniz:
              </p>
              <p>
                <span className="font-semibold text-[#0B1D3A]">E-posta: </span>
                <a
                  href="mailto:info@primesalespro.com"
                  className="text-[#D4AF37] hover:underline cursor-pointer"
                >
                  info@primesalespro.com
                </a>
              </p>
              <p className="mt-3">
                Prime Sales Pro, taleplerinizi KVKK kapsamında en geç 30 gün içerisinde
                sonuçlandıracaktır.
              </p>
            </section>

            <hr className="border-gray-200" />

            {/* Section 8 */}
            <section>
              <h4 className="font-bold text-[#0B1D3A] text-base mb-2">8. Güncellemeler</h4>
              <p>
                Prime Sales Pro, bu Aydınlatma Metni üzerinde gerekli gördüğü durumlarda
                değişiklik yapma hakkını saklı tutar. Güncel metin web sitesinde yayınlandığı
                tarihte yürürlüğe girer.
              </p>
            </section>
          </div>

          {/* Footer Button */}
          <div className="mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="w-full px-6 py-3 bg-gradient-to-r from-[#D4AF37] to-[#C9A961] text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-[#D4AF37]/30 transition-all duration-300"
            >
              Kapat
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KVKKModal;
